import type { FieldError } from 'react-hook-form';

import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { defineMessages, useIntl } from 'react-intl';
import ToggleButton from '@mui/material/ToggleButton';
import Typography from '@mui/material/Typography';
import { Iconify, get } from '@mfw/components';
import { Controller } from 'react-hook-form';
import Box from '@mui/material/Box';
import { onSurface } from '../../theme/onSurface';

export const moodMessages = defineMessages({
  label: {
    id: 'features.stats.moodLabel',
    defaultMessage: 'Mood',
  },
  bad: {
    id: 'features.stats.moodBad',
    defaultMessage: 'Bad',
  },
  ok: {
    id: 'features.stats.moodOk',
    defaultMessage: 'OK',
  },
  veryGood: {
    id: 'features.stats.moodVeryGood',
    defaultMessage: 'Very good',
  },
  great: {
    id: 'features.stats.moodGreat',
    defaultMessage: 'Great',
  },
});

// 1–4 ordinal — the `mood` registry key (Daily Activity). Faces, not stars:
// these are discrete named states, not a magnitude rating. Same icons and
// toggle style as the workout feedback mood (PlayerPage EffortSection).
export const MOOD_LEVELS = [
  { icon: 'mdi:emoticon-sad-outline', message: moodMessages.bad, value: 1 },
  { icon: 'mdi:emoticon-neutral-outline', message: moodMessages.ok, value: 2 },
  {
    icon: 'mdi:emoticon-happy-outline',
    message: moodMessages.veryGood,
    value: 3,
  },
  {
    icon: 'mdi:emoticon-excited-outline',
    message: moodMessages.great,
    value: 4,
  },
] as const;

export const MoodPicker = ({ name }: { name: string }) => {
  const { formatMessage } = useIntl();
  return (
    <Controller
      render={({ field, formState: { errors } }) => {
        const error = get<FieldError>(errors, name) ?? undefined;
        return (
          <Box>
            <Typography color="text.secondary" variant="body2" mb={0.5}>
              {formatMessage(moodMessages.label)}
            </Typography>
            <ToggleButtonGroup
              onChange={(_event, next: null | number) => field.onChange(next)}
              value={field.value ?? null}
              exclusive
              fullWidth
            >
              {MOOD_LEVELS.map((level) => (
                <ToggleButton
                  sx={{
                    '&.Mui-selected .component-iconify': {
                      color: onSurface.primary,
                    },
                    alignItems: 'flex-start',
                    flex: '1 1 0',
                    minWidth: 0,
                  }}
                  data-testid={`daily-activity-mood-${level.value}`}
                  aria-label={formatMessage(level.message)}
                  value={level.value}
                  key={level.value}
                >
                  <Box
                    flexDirection="column"
                    alignItems="center"
                    display="flex"
                    gap={0.5}
                  >
                    <Iconify icon={level.icon} size="medium" />
                    <Typography variant="caption">
                      {formatMessage(level.message)}
                    </Typography>
                  </Box>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            {error?.message && (
              <Typography color={onSurface.error} variant="caption">
                {error.message}
              </Typography>
            )}
          </Box>
        );
      }}
      name={name}
    />
  );
};

export default MoodPicker;
